import { defineStore } from 'pinia';
import { computed, ref, onScopeDispose } from 'vue';
import type { Day } from '@/service/weekplan/types';
import type { RecipeSort } from '@/service/recipe/findRecipes';
import type { UnitSystem } from '@/service/recipe/units';

export type ThemePreference = 'system' | 'light' | 'dark';

export const useSettingStore = defineStore(
  'settings',
  () => {
    const theme = ref<ThemePreference>('system');

    const systemDark = ref(
      window.matchMedia('(prefers-color-scheme: dark)').matches,
    );

    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => {
      systemDark.value = e.matches;
    };
    mql.addEventListener('change', handler);
    onScopeDispose(() => mql.removeEventListener('change', handler));

    const resolvedTheme = computed(() => {
      if (theme.value === 'system') {
        return systemDark.value ? 'dark' : 'light';
      }
      return theme.value;
    });

    const weekStartDay = ref<Day>('monday');

    // How ingredient amounts are shown
    const unitSystem = ref<UnitSystem>('original');

    // The recipe list's last filter (the search text is not kept)
    const recipeSort = ref<RecipeSort>('recent');
    const recipeTags = ref<string[]>([]);
    const favoritesOnly = ref(false);

    return {
      theme,
      resolvedTheme,
      weekStartDay,
      unitSystem,
      recipeSort,
      recipeTags,
      favoritesOnly,
    };
  },
  {
    persist: {
      storage: localStorage,
      pick: [
        'theme',
        'weekStartDay',
        'unitSystem',
        'recipeSort',
        'recipeTags',
        'favoritesOnly',
      ],
    },
  },
);
